import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  Link,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Tag,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
  useOutsideClick,
} from '@chakra-ui/react';
import { AddIcon, CalendarIcon, CloseIcon, WarningIcon } from '@chakra-ui/icons';
import { BiError } from 'react-icons/bi';
import { MdPeople } from 'react-icons/md';
import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { shallow } from 'zustand/shallow';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '#store';
import { CHECKOUT_STAGES, INITIAL_STATE } from '#constants';
import DateRangeSelector from '../Tracker/DateRangeSelector.jsx';
import FlexiDaysControl from '../Tracker/FlexiDaysControl.jsx';
import NightBookablePicker from './NightBookablePicker.jsx';
import TripStatsBanner from './TripStatsBanner.jsx';
import NzGreatWalkTripStatsBanner from './NzGreatWalkTripStatsBanner.jsx';
import { nzGreatWalkForTrack } from '../../utils/nzGreatWalkForTrack.js';

/** Parse a YYYY-MM-DD string into a local Date (no timezone shift). */
function parseLocalDate(dateString) {
  if (!dateString) return null;
  const [y, m, d] = dateString.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Format a local Date as YYYY-MM-DD (no timezone shift). */
function toIsoDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The day after a YYYY-MM-DD string, or '' when the input is empty/invalid. */
function nextDayString(dateString) {
  const d = parseLocalDate(dateString);
  if (!d) return '';
  d.setDate(d.getDate() + 1);
  return toIsoDateString(d);
}

const emptyNight = () => ({
  dateString: '',
  optionIds: [],
  acceptedChildrenByPlace: {},
});

/** Tracks whose places expose leaf bookable units (camping / refugio / …). */
const TRIP_LEAF_PICKER_CRAWLERS = new Set(['torres-hike']);

const idOf = (value) => {
  if (value == null) return '';
  if (typeof value === 'object') return String(value._id ?? value);
  return String(value);
};

function leafIdOf(leaf) {
  return idOf(leaf?.childId?._id ?? leaf?.childId);
}

/** Site-styled single-date picker that reuses the custom DateRangeSelector calendar. */
function NightDatePicker({ value, onChange }) {
  const ref = useRef();
  const [open, setOpen] = useState(false);
  useOutsideClick({ ref, handler: () => setOpen(false) });
  const selected = parseLocalDate(value);
  const defaultRange = selected ? [selected, selected] : [];
  return (
    <Flex direction="column" position="relative" ref={ref} w="100%" maxW="280px">
      <Flex
        borderWidth={1}
        borderColor={open ? 'brand.400' : 'blackAlpha.200'}
        borderRadius={8}
        px={3}
        py={2}
        align="center"
        justify="space-between"
        cursor="pointer"
        fontSize="sm"
        onClick={() => setOpen((o) => !o)}
      >
        <Text color={selected ? 'blackAlpha.900' : 'gray.400'}>
          {selected ? selected.toDateString() : 'Select date'}
        </Text>
        <CalendarIcon color="brand.500" ml={2} />
      </Flex>
      {open && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          zIndex={20}
          bg="white"
          shadow="xl"
          borderRadius="md"
          mt={1}
        >
          <DateRangeSelector
            single
            flexiDays={0}
            defaultRange={defaultRange}
            onChange={(range) => {
              const d = range?.[0];
              if (d) onChange(toIsoDateString(d));
              setOpen(false);
            }}
          />
        </Box>
      )}
    </Flex>
  );
}

/** Full-width place picker: selected chips on top, filterable scrollable list below. */
function NightPlacePicker({ huts, selectedIds, onToggle }) {
  const selectedSet = new Set(selectedIds.map(idOf));
  const trackableHuts = huts.filter((h) => h.trackable !== false);
  const contactOnlyHuts = huts.filter((h) => h.trackable === false);
  const selectedHuts = huts.filter((h) => selectedSet.has(idOf(h._id)));

  return (
    <Flex direction="column" gap={2} w="100%">
      <Flex justify="space-between" align="baseline">
        <Text fontSize="sm" fontWeight={600} color="gray.700">
          Acceptable places
        </Text>
        <Text fontSize="xs" color="gray.500">
          {selectedHuts.length
            ? `${selectedHuts.length} selected`
            : 'Select any that work'}
        </Text>
      </Flex>

      {selectedHuts.length > 0 && (
        <Wrap spacing={2}>
          {selectedHuts.map((hut) => (
            <WrapItem key={idOf(hut._id)}>
              <Tag
                size="md"
                variant="solid"
                colorScheme={hut.trackable === false ? 'orange' : 'brand'}
                cursor="pointer"
                onClick={() => onToggle(idOf(hut._id))}
              >
                <TagLabel>
                  {hut.name}
                  {hut.trackable === false ? ' · contact only' : ''}
                </TagLabel>
                <CloseIcon boxSize={2} ml={2} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <Box
        maxH="168px"
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        p={2}
        bg="gray.50"
      >
        <Wrap spacing={2}>
          {trackableHuts.map((hut) => {
            const id = idOf(hut._id);
            const selected = selectedSet.has(id);
            return (
              <WrapItem key={id}>
                <Tag
                  size="sm"
                  variant={selected ? 'solid' : 'outline'}
                  colorScheme="brand"
                  cursor="pointer"
                  onClick={() => onToggle(id)}
                >
                  <TagLabel>{hut.name}</TagLabel>
                </Tag>
              </WrapItem>
            );
          })}
          {trackableHuts.length === 0 && contactOnlyHuts.length === 0 && (
            <Text fontSize="sm" color="gray.500" px={1}>
              No places available
            </Text>
          )}
        </Wrap>
        {contactOnlyHuts.length > 0 && (
          <Box mt={3} pt={2} borderTopWidth="1px" borderColor="gray.200">
            <Text fontSize="xs" color="gray.500" mb={2} px={1}>
              Contact-only (no live calendar — alerts can&apos;t auto-match)
            </Text>
            <Wrap spacing={2}>
              {contactOnlyHuts.map((hut) => (
                <WrapItem key={idOf(hut._id)}>
                  <Tag
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    opacity={0.7}
                    cursor="not-allowed"
                    title="Email/phone booking only — not trackable"
                  >
                    <TagLabel>{hut.name}</TagLabel>
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        )}
      </Box>
    </Flex>
  );
}

export default function TripBuilder({
  onClose,
  editTracker,
  initialSearch = '',
  initialTrack = null,
}) {
  const {
    tripResults,
    noTripResults,
    getTripSuggestions,
    setOuting,
    setTrackerInput,
    updateTracker,
    getOutingStats,
    user,
    sub,
    toggleContactModal,
    setShowPricingModal,
    setCheckoutStage,
    setSteps,
    sendFBEvent,
    tawk,
  } = useUIStore(
    (state) => ({
      tripResults: state.tripResults,
      noTripResults: state.noTripResults,
      getTripSuggestions: state.getTripSuggestions,
      setOuting: state.setOuting,
      setTrackerInput: state.setTrackerInput,
      updateTracker: state.updateTracker,
      getOutingStats: state.getOutingStats,
      user: state.user,
      sub: state.sub,
      toggleContactModal: state.toggleContactModal,
      setShowPricingModal: state.setShowPricingModal,
      setCheckoutStage: state.setCheckoutStage,
      setSteps: state.setSteps,
      sendFBEvent: state.sendFBEvent,
      tawk: state.tawk,
    }),
    shallow
  );
  const navigate = useNavigate();
  const isEdit = !!editTracker;

  const [search, setSearch] = useState(
    () => initialTrack?.name || initialSearch || ''
  );
  const [showResults, setShowResults] = useState(false);
  const [track, setTrack] = useState(() => initialTrack || null);
  const [nights, setNights] = useState([emptyNight()]);
  const [groupSize, setGroupSize] = useState(1);
  const [flexi, setFlexi] = useState(0);
  const [error, setError] = useState('');

  const debouncedSearch = useCallback(
    debounce((q) => getTripSuggestions(q), 400),
    []
  );

  // Create flow: if opened from search with a query but no single match,
  // seed tripResults so the trail picker shows immediately.
  useEffect(() => {
    if (editTracker || initialTrack || !initialSearch?.trim()) return;
    getTripSuggestions(initialSearch.trim());
    setShowResults(true);
  }, []);

  // Prefill from an existing trip tracker when editing, and load the parent
  // track's children so all huts remain selectable.
  useEffect(() => {
    if (!editTracker) return;
    setGroupSize(editTracker.groupSize ?? 1);
    setFlexi(editTracker.flexiDaysAfter ?? editTracker.flexiDaysBefore ?? 0);
    setNights(
      (editTracker.nights ?? []).map((n) => {
        const acceptedChildrenByPlace = {};
        for (const o of n.options ?? []) {
          const placeId = idOf(o.childId?._id ?? o.childId);
          if (!placeId) continue;
          const leaves = (o.acceptedChildren ?? [])
            .map((leaf) => ({
              childModel: leaf.childModel || 'Campsites',
              childId: leafIdOf(leaf),
              name: leaf.name || leaf.childId?.name || 'Option',
            }))
            .filter((leaf) => leaf.childId);
          if (leaves.length) acceptedChildrenByPlace[placeId] = leaves;
        }
        return {
          dateString: n.dateString,
          optionIds: (n.options ?? [])
            .map((o) => idOf(o.childId?._id ?? o.childId))
            .filter(Boolean),
          acceptedChildrenByPlace,
        };
      })
    );
    setSearch(editTracker.outing?.name ?? '');
    setTrack(null);
    if (editTracker.outing?.name) getTripSuggestions(editTracker.outing.name);
  }, [editTracker?._id]);

  // Once trip search results arrive in edit mode, bind the matching track
  // (with its children) without disturbing the prefilled nights.
  useEffect(() => {
    if (!editTracker || track || !tripResults?.length) return;
    const match = tripResults.find(
      (t) =>
        (editTracker.outing?.entity_id != null &&
          t.entity_id === editTracker.outing.entity_id) ||
        t._id === editTracker.outing?._id
    );
    if (match) {
      setTrack(match);
      if (match._id) getOutingStats({ outingId: match._id });
    }
  }, [tripResults, editTracker, track]);

  // Create flow opened with a pre-selected track — load stats once.
  useEffect(() => {
    if (editTracker || !initialTrack?._id) return;
    getOutingStats({ outingId: initialTrack._id });
  }, []);

  // Normalize the track's curated tripOptions into the shape NightPlacePicker
  // expects ({ _id, name }), carrying childModel through for the submit payload.
  const huts = (track?.tripOptions ?? []).map((opt) => ({
    _id: idOf(opt.childId),
    name: opt.name,
    childModel: opt.childModel,
    entity_type: opt.entity_type,
    trackable: opt.trackable !== false,
    reservable: opt.reservable !== false,
    stage: opt.stage ?? null,
  }));
  const supportsLeafPicker = TRIP_LEAF_PICKER_CRAWLERS.has(track?.crawlerType);
  const nzWalk = track ? nzGreatWalkForTrack(track) : null;

  const selectTrack = (t) => {
    setTrack(t);
    setSearch(t.name);
    setShowResults(false);
    setNights([emptyNight()]);
    setError('');
    // Load park-level cancellation stats (backend falls back via parentId).
    if (t?._id) getOutingStats({ outingId: t._id });
  };

  const setNightDate = (idx, dateString) => {
    setNights((prev) =>
      prev.map((n, i) => (i === idx ? { ...n, dateString } : n))
    );
  };

  const toggleNightOption = (idx, hutId) => {
    const id = idOf(hutId);
    const hut = huts.find((h) => idOf(h._id) === id);
    // Contact-only places can never satisfy a live trip scan — ignore toggles.
    if (hut && hut.trackable === false) return;
    setNights((prev) =>
      prev.map((n, i) => {
        if (i !== idx) return n;
        const ids = n.optionIds.map(idOf);
        const has = ids.includes(id);
        const acceptedChildrenByPlace = {
          ...(n.acceptedChildrenByPlace || {}),
        };
        if (has) {
          delete acceptedChildrenByPlace[id];
        }
        return {
          ...n,
          optionIds: has ? ids.filter((x) => x !== id) : [...ids, id],
          acceptedChildrenByPlace,
        };
      })
    );
  };

  const setNightAcceptedChildren = (nightIdx, placeId, leaves) => {
    setNights((prev) =>
      prev.map((n, i) => {
        if (i !== nightIdx) return n;
        return {
          ...n,
          acceptedChildrenByPlace: {
            ...(n.acceptedChildrenByPlace || {}),
            [idOf(placeId)]: leaves,
          },
        };
      })
    );
  };

  const addNight = () =>
    setNights((prev) => {
      const last = prev[prev.length - 1];
      return [...prev, { dateString: nextDayString(last?.dateString), optionIds: [] }];
    });
  const removeNight = (idx) =>
    setNights((prev) => prev.filter((_, i) => i !== idx));

  const validate = () => {
    if (!track?._id) return 'Please select a trail';
    if (!nights.length) return 'Add at least one night';
    let prev = -Infinity;
    for (let i = 0; i < nights.length; i++) {
      const n = nights[i];
      const d = parseLocalDate(n.dateString);
      if (!d) return `Pick a date for night ${i + 1}`;
      if (!n.optionIds.length) return `Pick at least one place for night ${i + 1}`;
      if (d.getTime() <= prev) return 'Night dates must be in increasing order';
      const contactOnly = n.optionIds
        .map((id) => huts.find((h) => idOf(h._id) === idOf(id)))
        .filter((h) => h && h.trackable === false);
      if (contactOnly.length) {
        return `Night ${i + 1}: ${contactOnly.map((h) => h.name).join(', ')} ${
          contactOnly.length === 1 ? 'is' : 'are'
        } contact-only — pick a trackable rifugio so we can alert you`;
      }
      prev = d.getTime();
    }
    return '';
  };

  const isUserVerified = () => {
    if (!user?.contacts?.[0]) return false;
    return user.contacts.some((c) => c.type === 'phone' && c.verified);
  };

  const handleSubmit = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError('');

    const optionById = Object.fromEntries(
      (huts ?? []).map((h) => [idOf(h._id), h])
    );
    const nightsPayload = nights.map((n) => ({
      dateString: n.dateString,
      options: n.optionIds.map((rawId) => {
        const id = idOf(rawId);
        const opt = optionById[id];
        const acceptedChildren = (n.acceptedChildrenByPlace?.[id] ?? []).map(
          (leaf) => ({
            childModel: leaf.childModel || 'Campsites',
            childId: idOf(leaf.childId),
            name: leaf.name || 'Option',
          })
        );
        return {
          // Point directly at the real bookable child (hut Outing or permit
          // division). `name` is denormalized so the summary UI doesn't depend
          // on populate.
          childModel: opt?.childModel || 'Outings',
          childId: id,
          name: opt?.name || 'Place',
          ...(acceptedChildren.length ? { acceptedChildren } : {}),
        };
      }),
    }));

    if (isEdit) {
      updateTracker({
        tracker: {
          _id: editTracker._id,
          type: 'trip',
          nights: nightsPayload,
          groupSize,
          flexiDaysBefore: flexi,
          flexiDaysAfter: flexi,
        },
      });
      onClose?.();
      return;
    }

    setOuting(track);
    setTrackerInput({
      ...INITIAL_STATE.trackerInput,
      type: 'trip',
      nights: nightsPayload,
      groupSize,
      flexiDaysBefore: flexi,
      flexiDaysAfter: flexi,
      notificationPref: ['text', 'call'],
      startDate: parseLocalDate(nights[0].dateString),
      endDate: parseLocalDate(nights[nights.length - 1].dateString),
    });

    setSteps(['pricingModal', 'checkout']);
    sendFBEvent({ eventName: 'AddToCart', commerce: 'intent' });
    onClose?.();

    if (!isUserVerified()) {
      toggleContactModal(true);
    } else if (sub?._id) {
      setShowPricingModal(false);
      navigate('/checkout');
      setCheckoutStage(CHECKOUT_STAGES[0]);
    } else {
      setShowPricingModal(true);
    }
  };

  return (
    <Flex align="stretch" direction="column" w="100%" gap={4}>
      {track && nzWalk && <NzGreatWalkTripStatsBanner walk={nzWalk} />}
      {track && !nzWalk && <TripStatsBanner />}
      <Text fontSize="sm" color="gray.600">
        Track a whole multi-night hike. Pick places for each night and we&apos;ll
        alert you when the full itinerary opens up on the same dates.
      </Text>

      {/* Track search */}
      <Box position="relative">
        <FormControl>
          <FormLabel fontSize="sm" mb={1}>
            Trail
          </FormLabel>
          <Input
            placeholder="Search a trail (e.g. Alta Via 1, Routeburn, Wonderland)"
            value={search}
            autoComplete="off"
            onChange={(e) => {
              setSearch(e.target.value);
              setTrack(null);
              setShowResults(true);
              debouncedSearch(e.target.value);
            }}
            onFocus={() => setShowResults(true)}
          />
        </FormControl>
        {showResults && !track && tripResults?.length > 0 && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            bg="white"
            boxShadow="lg"
            borderRadius="md"
            zIndex={5}
            mt={1}
            maxH="260px"
            overflowY="auto"
          >
            {tripResults.map((t) => (
              <Box
                key={t.entity_id}
                px={3}
                py={2}
                _hover={{ bg: 'brand.50', cursor: 'pointer' }}
                onClick={() => selectTrack(t)}
              >
                <Text fontWeight={600}>{t.name}</Text>
                <Text fontSize="xs" color="gray.500">
                  {t.parent_name || t.state_code}
                  {t.tripOptions?.length
                    ? ` · ${t.tripOptions.length} places`
                    : ''}
                </Text>
              </Box>
            ))}
          </Box>
        )}
        {showResults && !track && noTripResults && search.trim() && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            bg="white"
            boxShadow="lg"
            borderRadius="md"
            zIndex={5}
            mt={1}
            px={4}
            py={3}
          >
            <Flex align="center" mb={1}>
              <Icon as={BiError} mr={2} boxSize={5} color="red.600" />
              <Text fontWeight="semibold">No Results Found</Text>
            </Flex>
            <Text fontSize="sm" color="gray.700">
              Try another name, or{' '}
              <Link
                textDecor="underline"
                fontStyle="italic"
                color="brand.600"
                onMouseDown={(e) => {
                  e.preventDefault();
                  tawk?.current?.maximize?.();
                }}
              >
                talk to support to request addition
              </Link>
              .
            </Text>
          </Box>
        )}
      </Box>

      {track && (
        <>
          <HStack spacing={6} flexWrap="wrap">
            <Flex align="center">
              <Icon as={MdPeople} color="brand.500" boxSize={6} mr={2} />
              <NumberInput
                size="sm"
                maxW={20}
                min={1}
                max={50}
                value={groupSize}
                onChange={(_, n) => setGroupSize(Number.isNaN(n) ? 1 : n)}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </Flex>
            <FlexiDaysControl
              editMode={isEdit}
              value={flexi}
              onChange={setFlexi}
            />
          </HStack>

          {/* Nights */}
          <Flex direction="column" gap={3}>
            {nights.map((night, idx) => (
              <Box
                key={idx}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
              >
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontWeight={600} color="brand.700">
                    Night {idx + 1}
                  </Text>
                  {nights.length > 1 && (
                    <IconButton
                      aria-label="Remove night"
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      icon={<CloseIcon boxSize={2.5} />}
                      onClick={() => removeNight(idx)}
                    />
                  )}
                </Flex>
                <Flex direction="column" gap={3}>
                  <NightDatePicker
                    value={night.dateString}
                    onChange={(dateString) => setNightDate(idx, dateString)}
                  />
                  <NightPlacePicker
                    huts={huts}
                    selectedIds={night.optionIds}
                    onToggle={(hutId) => toggleNightOption(idx, hutId)}
                  />
                  {supportsLeafPicker &&
                    night.optionIds.map((placeId) => {
                      const hut = huts.find((h) => idOf(h._id) === idOf(placeId));
                      if (!hut) return null;
                      return (
                        <NightBookablePicker
                          key={`${idx}-${idOf(placeId)}`}
                          placeOutingId={idOf(placeId)}
                          placeName={hut.name}
                          entityType={hut.entity_type || 'campground'}
                          selectedChildren={
                            night.acceptedChildrenByPlace?.[idOf(placeId)] ?? []
                          }
                          groupSize={groupSize}
                          onChange={(leaves) =>
                            setNightAcceptedChildren(idx, placeId, leaves)
                          }
                        />
                      );
                    })}
                </Flex>
              </Box>
            ))}
            <Button
              leftIcon={<AddIcon />}
              size="sm"
              variant="ghost"
              colorScheme="brand"
              alignSelf="flex-start"
              onClick={addNight}
            >
              Add night
            </Button>
          </Flex>

          {error && (
            <Flex align="center" color="red.500">
              <WarningIcon mr={2} />
              <Text>{error}</Text>
            </Flex>
          )}

          <Button colorScheme="brand" onClick={handleSubmit} textColor="white">
            {isEdit ? 'Update Trip' : 'Get Alerts for this Trip'}
          </Button>
        </>
      )}
    </Flex>
  );
}
